/*
  Rod Finder prompt.

  Opens once a visitor has looked at TWO distinct rod PDPs in a session and has
  not been offered it in the last 7 days. One ask, then it leaves them alone.

  Storage:
    sessionStorage aof_rfp_seen   JSON array of rod handles viewed this session
    sessionStorage aof_rfp_done   set once shown, so it cannot reopen this session
    localStorage   aof_rfp_snooze epoch ms; nothing opens before this
*/
(() => {
  const SEEN = 'aof_rfp_seen';
  const DONE = 'aof_rfp_done';
  const SNOOZE = 'aof_rfp_snooze';
  const TRIGGER_AT = 2; // distinct rod PDPs
  const SNOOZE_DAYS = 7;
  const DELAY_MS = 2500;

  const read = (store, key, fallback) => {
    try {
      const raw = store.getItem(key);
      return raw === null ? fallback : JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  };
  const write = (store, key, value) => {
    try {
      store.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode — the prompt simply never fires */
    }
  };

  class AofRodFinderPrompt extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.querySelectorAll('[data-rfp-dismiss]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      const accept = this.querySelector('[data-rfp-accept]');
      if (accept) accept.addEventListener('click', () => this.snooze());
      // Escape fires `cancel`; the backdrop is a click on the dialog itself.
      // Both snooze through close() rather than through the dialog's own
      // `close` event, which is NOT reliably fired by every engine — trusting
      // it silently loses the 7-day snooze and the prompt nags every session.
      this.dialog.addEventListener('cancel', () => this.snooze());
      this.dialog.addEventListener('close', () => this.snooze());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      if (this.shouldOpen()) this.timer = setTimeout(() => this.open(), DELAY_MS);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    shouldOpen() {
      // Never on the Rod Finder itself.
      if (window.location.pathname.indexOf(this.dataset.url) === 0) return false;
      if (read(sessionStorage, DONE, false)) return false;
      if (Date.now() < (read(localStorage, SNOOZE, 0) || 0)) return false;

      const handle = this.dataset.productHandle;
      const seen = read(sessionStorage, SEEN, []);
      if (handle && seen.indexOf(handle) === -1) {
        seen.push(handle);
        write(sessionStorage, SEEN, seen);
      }
      return seen.length >= TRIGGER_AT;
    }

    open() {
      this.opener = document.activeElement;
      write(sessionStorage, DONE, true);
      this.dialog.showModal();
    }

    close() {
      this.snooze();
      if (this.dialog.open) this.dialog.close();
    }

    snooze() {
      // Idempotent: every dismissal path calls it, and the backstop listeners
      // may call it again. Writing the same stamp twice costs nothing.
      write(localStorage, SNOOZE, Date.now() + SNOOZE_DAYS * 864e5);
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    }
  }

  if (!customElements.get('aof-rod-finder-prompt')) {
    customElements.define('aof-rod-finder-prompt', AofRodFinderPrompt);
  }
})();
